<!--
	RouteLine — a job's route printed as one readout line under a rule: a mono
	label, then the steps joined by short drawn dashes. No boxes, no icons. The
	`live` route takes the cut colour; a `tested` step carries the filled dot.
-->
<script lang="ts">
	export interface RouteStep {
		name: string;
		/** Mono qualifier after the name: a file format, a licence note. */
		note?: string;
		/** Equipment we run ourselves. */
		tested?: boolean;
	}

	let {
		label,
		steps,
		live = false
	}: { label: string; steps: RouteStep[]; live?: boolean } = $props();
</script>

<div class="route" class:route--live={live}>
	<p class="route__label">{label}</p>
	<ol class="route__steps">
		{#each steps as step (step.name)}
			<li>
				{#if step.tested}<span class="route__dot" aria-hidden="true"></span>{/if}
				<span class="route__name">{step.name}</span>
				{#if step.note}<span class="route__note">{step.note}</span>{/if}
			</li>
		{/each}
	</ol>
</div>

<style>
	.route {
		display: grid;
		gap: 0.5rem 2rem;
		padding-block: 1rem;
		border-top: 1px solid var(--color-app-700);
	}
	@media (min-width: 800px) {
		.route {
			grid-template-columns: 9rem minmax(0, 1fr);
			align-items: baseline;
		}
	}
	.route__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.route--live .route__label {
		color: var(--color-cut-400);
	}
	.route__steps {
		list-style: none;
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 0.375rem 0;
	}
	.route__steps li {
		display: inline-flex;
		align-items: baseline;
		gap: 0.5rem;
	}
	.route__steps li + li::before {
		content: '';
		align-self: center;
		width: clamp(1rem, 2.5vw, 2.25rem);
		height: 1px;
		margin-inline: 0.75rem 0.25rem;
		background: var(--color-app-500);
	}
	.route--live li + li::before {
		background: color-mix(in srgb, var(--color-cut-400) 60%, transparent);
	}
	.route__name {
		color: var(--color-bone-100);
		letter-spacing: -0.01em;
	}
	.route__note {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-bone-500);
	}
	.route__dot {
		align-self: center;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--color-cut-400);
	}
</style>
